import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { ConnectionsResponse } from '../types';
import { formatDateTime, formatRelative } from '../format';
import { ConnectAgencyForm, ConnectSubAccountForm } from '../components/ConnectForms';

/** Private Integration Tokens, shown under Sub-accounts. */
export function Connections({ onChanged, canEdit = true }: { onChanged: () => void; canEdit?: boolean }) {
  const [data, setData] = useState<ConnectionsResponse | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [adding, setAdding] = useState<'subaccount' | 'agency' | null>('subaccount');

  const refresh = useCallback(() => {
    api
      .connections()
      .then(setData)
      .catch(() => undefined);
  }, []);

  useEffect(refresh, [refresh]);

  async function verify() {
    setVerifying(true);
    try {
      await api.verifyConnections();
      refresh();
    } finally {
      setVerifying(false);
    }
  }

  async function remove(id: string) {
    await api.deleteConnection(id);
    refresh();
    onChanged();
  }

  const locationTokens = data?.tokens.filter((token) => token.kind === 'location') ?? [];
  const agencyTokens = data?.tokens.filter((token) => token.kind === 'agency') ?? [];

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>Private Integration Tokens</h2>
          <button type="button" className="btn btn-sm" onClick={verify} disabled={!canEdit || verifying || !data?.tokens.length}>
            {verifying ? 'Checking…' : 'Re-check all'}
          </button>
        </div>
        <p className="card-sub">
          HighLevel issues a Private Integration Token at one level. Sub-account tokens read calls,
          appointments, contacts, users and calendars. An agency token discovers your sub-accounts and
          can create one. Tokens are encrypted before they are stored and never sent back to the browser.
        </p>

        {data?.oauthInstall ? (
          <div className="banner" style={{ marginBottom: 14 }}>
            <span>
              <strong>Marketplace app installed</strong> for agency {data.oauthInstall.companyId}, connected{' '}
              {formatRelative(data.oauthInstall.connectedAt)}. Sub-account tokens are minted automatically,
              so per-sub-account tokens below are optional.
            </span>
          </div>
        ) : null}

        {!data?.hasAgencyCredentials && locationTokens.length > 0 ? (
          <div className="banner" style={{ marginBottom: 14 }}>
            <span>
              No agency credentials, so sub-accounts are not discovered automatically and{' '}
              <strong>Add sub-account</strong> is unavailable. Everything else works — connect each
              sub-account below.
            </span>
          </div>
        ) : null}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Level</th>
                <th>Sub-account / agency</th>
                <th>Label</th>
                <th>Token</th>
                <th>Added</th>
                <th>Last checked</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...agencyTokens, ...locationTokens].map((token) => (
                <tr key={token.id}>
                  <td className="nowrap">{token.kind === 'agency' ? 'Agency' : 'Sub-account'}</td>
                  <td>
                    <span className="cell-strong">
                      {token.kind === 'agency' ? token.companyId : (token.locationName ?? token.locationId)}
                    </span>
                    {token.kind === 'location' && token.locationName ? (
                      <div className="cell-sub">{token.locationId}</div>
                    ) : null}
                  </td>
                  <td>{token.label ?? '—'}</td>
                  <td className="nowrap">
                    <code className="small">…{token.hint}</code>
                  </td>
                  <td className="nowrap">{formatDateTime(token.createdAt)}</td>
                  <td className="nowrap">{formatRelative(token.lastVerifiedAt)}</td>
                  <td>
                    {token.lastError ? (
                      <span className="tag" title={token.lastError} style={{ color: 'var(--danger-text)' }}>
                        <span className="dot" style={{ background: 'var(--outcome-failed)' }} aria-hidden="true" />
                        Failing
                      </span>
                    ) : (
                      <span className="tag">
                        <span className="dot" style={{ background: 'var(--outcome-answered)' }} aria-hidden="true" />
                        Working
                      </span>
                    )}
                  </td>
                  <td>
                    {canEdit ? (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(token.id)}>
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {data && data.tokens.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty">
                      No tokens yet.
                      {data.oauthInstall ? ' The marketplace app is handling authentication.' : ''}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {canEdit ? (
        <div className="card stack">
          <div className="card-head">
            <h2>Add a token</h2>
            <div className="chips">
              <button
                type="button"
                className="chip"
                aria-pressed={adding === 'subaccount'}
                onClick={() => setAdding('subaccount')}
              >
                {adding === 'subaccount' ? '✓ ' : ''}Sub-account
              </button>
              <button
                type="button"
                className="chip"
                aria-pressed={adding === 'agency'}
                onClick={() => setAdding('agency')}
              >
                {adding === 'agency' ? '✓ ' : ''}Agency
              </button>
            </div>
          </div>

          {adding === 'subaccount' ? (
            <ConnectSubAccountForm
              onConnected={() => {
                refresh();
                onChanged();
              }}
            />
          ) : (
            <ConnectAgencyForm
              onConnected={() => {
                refresh();
                onChanged();
              }}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}
